import type { ParseKeys } from 'i18next';
import {
  ChartColumn,
  FlaskConical,
  FolderOpen,
  Home,
  Mail,
  Receipt,
  ShieldCheck,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ARCHIVE_GATE } from '@/archive/access';
import { COMMUNICATIONS_GATE } from '@/communications/access';
import {
  EXPENSES_CLOSINGS_GATE,
  EXPENSES_ENTRY_GATE,
  EXPENSES_GATE,
  EXPENSES_MANAGE_GATE,
} from '@/expenses/access';
import { TOOLS_GATE } from '@/tools/access';
import { CLEAN_TEXT_AVAILABLE } from '@/tools/clean-text/availability';

/**
 * A key that actually exists in `locales/*\/shell.json`, per this workspace's `i18next.d.ts`
 * module augmentation. Typing the catalog's labels as this rather than as `string` is what turns a
 * mistyped or removed translation key into a compile error instead of a `nav.finnance` rendered
 * literally into the rail — the failure mode i18next's own dev-time missing-key warning only
 * catches if someone is watching the console.
 */
export type ShellTranslationKey = ParseKeys<'shell'>;

/** The translator the resolver needs: exactly `useTranslation('shell').t`, narrowed to one call
 * shape so the pure resolver does not depend on i18next's full overloaded `TFunction`. */
export type ShellTranslate = (key: ShellTranslationKey) => string;

/**
 * THE navigation model. Every entry the shell can show — rail, bottom bar, breadcrumb, command
 * palette — is declared here and nowhere else.
 *
 * This file exists because of what it replaces. The previous shell hardcoded six `<NavLink>`s
 * inside `Shell.tsx`, each with its own inline gate written as JSX (`isWorkspaceEmail(...) && …`,
 * `financeCapability.status === 'allowed' && …`, or no gate at all), and each of the four
 * consumers of that list would have had to repeat the same six entries and the same six gates.
 * Adding a module meant editing the shell in four places and getting the gate right four times.
 * Now it means adding one object to the array below.
 *
 * TWO LEVELS, NOT ONE (proposal decision 1)
 * A module is level one; the module's own screens are level two. The previous rail mixed them:
 * `Studi`, `Pool date`, `Guida rapida` and `Monitoraggio CRO` were four siblings of `Finanza`,
 * which itself hid four views that the navigation never mentioned. One module contributed four
 * top-level entries while another contributed one for five screens.
 *
 * WHAT `path` AND `basePath` EACH ANSWER — they are different questions and conflating them is the
 * bug this comment exists to prevent:
 *   `path`     — where the entry NAVIGATES. For a module with sections this is its landing screen,
 *                which is the first section, not necessarily an index route.
 *   `basePath` — the module's TERRITORY, used to decide which module is active and therefore which
 *                sections the rail shows. `clinical-study` lands on `/clinical-study/studies` but
 *                owns everything under `/clinical-study`, including the study-detail and
 *                participant-calendar screens that are not sections of their own.
 *
 * COUNTERS ARE NOT DECLARED HERE, AND THAT IS DELIBERATE.
 * This file is static; a counter is a live, per-organization read that changes on its own. A number
 * written into a static catalog would be a hardcoded number in navigation, which is read as fact —
 * the exact failure the counter work existed to avoid. The catalog therefore carries no `count`
 * field at all: counters are contributed by `shell/counters/use-attention-counts.ts`, keyed by the
 * module `id` below, and attached to the resolved list by `withCounts`. Giving a module a counter
 * means adding a source there, never a literal here.
 */

/**
 * How a module decides whether it may appear. A closed union rather than a predicate function, so
 * that the resolver — and its tests — enumerate the gates exhaustively and a new gate kind cannot
 * be added without the compiler naming every place that must handle it.
 *
 * Every one of these is CONVENIENCE ONLY (FR-6.3). Each module's route enforces its own
 * entitlement and permission checks server-side regardless of what the rail renders, which is why
 * every gated module below also has a route gate — a hidden entry leaves the URL reachable.
 */
export type ModuleGate =
  /** Reachable by every authenticated User. */
  | { readonly kind: 'always' }
  /** The signed-in account's own email domain decides (`isWorkspaceEmail`). */
  | { readonly kind: 'workspace-domain' }
  /** The Finance capability read decides — the organization's entitlement AND the caller's role. */
  | { readonly kind: 'finance-capability' }
  /** The platform operator only (`useIsOperator`). */
  | { readonly kind: 'operator' }
  /**
   * Anyone who administers a place — a global, tenant or organization grant, from
   * `/v1/administration/scopes` (FR-AD-3) — and the platform operator, whose screens stay reachable
   * until slice 2 of the administration area replaces them (FR-AD-12).
   */
  | { readonly kind: 'administrator' }
  /**
   * The gated-navigation filter over `moduleRegistry` (FR-6.1/FR-6.2): visible only when the
   * `moduleId` is entitled AND the `requiredPermission` is held. No module uses this yet — the
   * capability-fetching plumbing does not exist — and it stays in the union because it is the
   * mechanism the shell was designed around, not a placeholder to delete.
   */
  | { readonly kind: 'registry'; readonly moduleId: string; readonly requiredPermission: string }
  /**
   * Visible when the caller holds AT LEAST ONE of these permission codes, as reported by the
   * module's own "what may I do" read. This is the general shape the Human asked for on
   * 2026-09-14 — "le voci di menù devono sempre rispecchiare il livello di accesso, in generale" —
   * and it is general in the way that matters: it takes codes, not a module name, so a module
   * adopts it by pointing at its own read rather than by earning a bespoke gate kind.
   *
   * WHY IT DOES NOT REPLACE `finance-capability` YET, which is the obvious next question. Finance's
   * capability read does not return permission CODES; it returns a small set of booleans, three of
   * which are typed as the literal `true`. So finance cannot feed this gate until that payload
   * carries codes, and what those codes should be per section is a granularity decision the Human
   * has explicitly reserved. Leaving finance on its own gate is therefore the honest state, not an
   * oversight — and this comment is where the next person finds out why.
   */
  | { readonly kind: 'permission'; readonly anyOf: readonly string[] };

export type CatalogSection = {
  readonly id: string;
  readonly labelKey: ShellTranslationKey;
  readonly path: string;
  /** `true` when the path is an index route and must match exactly rather than by prefix. */
  readonly end?: boolean;
  /**
   * What this one screen needs, when the module's own gate is not the whole answer.
   *
   * Absent means the module gate decides, which is how every section behaved until 2026-09-21.
   * Finance is the first module where opening it and seeing all of it are different questions:
   * Human decision 27 requires each role to see only its own surfaces, and an accountant who
   * legitimately holds three of nine codes must not be offered the other six.
   */
  readonly gate?: ModuleGate;
};

export type CatalogModule = {
  readonly id: string;
  readonly labelKey: ShellTranslationKey;
  /** Where the rail entry navigates — see the `path` vs `basePath` note above. */
  readonly path: string;
  /** The module's territory, for deciding which module is active. */
  readonly basePath: string;
  /** `true` when `basePath` must match exactly — only the root screen needs it. */
  readonly end?: boolean;
  readonly icon: LucideIcon;
  readonly gate: ModuleGate;
  readonly sections: readonly CatalogSection[];
};

export const MODULE_CATALOG: readonly CatalogModule[] = [
  {
    id: 'home',
    labelKey: 'nav.home',
    path: '/',
    basePath: '/',
    end: true,
    icon: Home,
    gate: { kind: 'always' },
    sections: [],
  },
  {
    // SL-0053. Entitlement-gated, so the capability READ decides — the organization's entitlement
    // and the caller's role, never the email suffix. `FinanceGate` gates the route.
    id: 'finance',
    labelKey: 'nav.finance',
    path: '/finance',
    basePath: '/finance',
    // Brand Kit § Iconography: Finanza → `ChartColumn`.
    icon: ChartColumn,
    gate: { kind: 'finance-capability' },
    // The four views of the CEO's dashboard plus its reference data. Each is its own URL, not a
    // tab inside one page, so a figure can be linked to and the browser's back button means what
    // it says. These five used to be declared a second time in `FinanceSectionLayout`'s own tab
    // bar; that layout is gone and this is now their only declaration.
    sections: [
      {
        id: 'dashboard',
        labelKey: 'nav.financeSections.dashboard',
        path: '/finance',
        end: true,
        gate: { kind: 'permission', anyOf: ['finance:dashboard:read'] },
      },
      {
        id: 'income-statement',
        labelKey: 'nav.financeSections.incomeStatement',
        path: '/finance/conto-economico',
        gate: { kind: 'permission', anyOf: ['finance:income-statement:read'] },
      },
      {
        id: 'cash-flow',
        labelKey: 'nav.financeSections.cashFlow',
        path: '/finance/flusso-di-cassa',
        gate: { kind: 'permission', anyOf: ['finance:cash-flow:read'] },
      },
      {
        id: 'commesse',
        labelKey: 'nav.financeSections.commesse',
        path: '/finance/commesse',
        gate: { kind: 'permission', anyOf: ['finance:commesse:read'] },
      },
      {
        id: 'reference',
        labelKey: 'nav.financeSections.reference',
        path: '/finance/dati-di-riferimento',
        gate: { kind: 'permission', anyOf: ['finance:reference-registers:read'] },
      },
      {
        // Human decision 35. Its own entry rather than rows inside the others, because the point
        // of the decision is that distributions become something to SHOW OR HIDE — which the
        // movement filter, being equality-only, could never express by exclusion.
        id: 'dividends',
        labelKey: 'nav.financeSections.dividends',
        path: '/finance/dividendi',
        gate: { kind: 'permission', anyOf: ['finance:dividends:read'] },
      },
      {
        // SL-0058 Unit 7. Its own URL like the five above, so the register a month produced can be
        // linked to and the back button means what it says.
        id: 'prima-nota',
        labelKey: 'nav.financeSections.primaNota',
        path: '/finance/prima-nota',
        gate: { kind: 'permission', anyOf: ['finance:prima-nota:read'] },
      },
    ],
  },
  {
    // «Note spese» (release 1, decision 131): right after Finance, whose organisation-level
    // expenses it feeds. The gate is `expenses:expense:submit` (open to organisation members) or
    // `expenses:expense:read-all`, resolved by the server and read in `useExpensesPermissions`.
    id: 'expenses',
    labelKey: 'nav.expenses',
    path: '/expenses',
    basePath: '/expenses',
    icon: Receipt,
    gate: EXPENSES_GATE,
    sections: [
      {
        id: 'list',
        labelKey: 'nav.expensesSections.list',
        path: '/expenses',
        end: true,
        gate: EXPENSES_ENTRY_GATE,
      },
      { id: 'reports', labelKey: 'nav.expensesSections.reports', path: '/expenses/reports' },
      {
        id: 'approvals',
        labelKey: 'nav.expensesSections.approvals',
        path: '/expenses/approvals',
        gate: { kind: 'permission', anyOf: ['expenses:report:approve'] },
      },
      {
        id: 'closings',
        labelKey: 'nav.expensesSections.closings',
        path: '/expenses/closings',
        gate: EXPENSES_CLOSINGS_GATE,
      },
      {
        id: 'new',
        labelKey: 'nav.expensesSections.new',
        path: '/expenses/new',
        gate: EXPENSES_ENTRY_GATE,
      },
      {
        id: 'routes',
        labelKey: 'nav.expensesSections.routes',
        path: '/expenses/routes',
        gate: EXPENSES_ENTRY_GATE,
      },
      {
        id: 'vehicles',
        labelKey: 'nav.expensesSections.vehicles',
        path: '/expenses/vehicles',
        gate: EXPENSES_ENTRY_GATE,
      },
      {
        id: 'mileage-settings',
        labelKey: 'nav.expensesSections.mileageSettings',
        path: '/expenses/settings/mileage',
        gate: EXPENSES_MANAGE_GATE,
      },
      {
        id: 'flat-rate-settings',
        labelKey: 'nav.expensesSections.flatRateSettings',
        path: '/expenses/settings/flat-rate',
        gate: EXPENSES_MANAGE_GATE,
      },
      {
        id: 'purposes',
        labelKey: 'nav.expensesSections.purposes',
        path: '/expenses/settings/purposes',
        gate: EXPENSES_MANAGE_GATE,
      },
      {
        id: 'note-types',
        labelKey: 'nav.expensesSections.noteTypes',
        path: '/expenses/settings/note-types',
        gate: EXPENSES_MANAGE_GATE,
      },
      {
        id: 'reserved-purposes',
        labelKey: 'nav.expensesSections.reservedPurposes',
        path: '/expenses/settings/reserved-purposes',
        gate: EXPENSES_MANAGE_GATE,
      },
      {
        // Decision 192: Gestione fills in people's address and tax code for the note's PDF.
        id: 'owner-details',
        labelKey: 'nav.expensesSections.ownerDetails',
        path: '/expenses/people',
        gate: EXPENSES_MANAGE_GATE,
      },
    ],
  },
  {
    // The four entries that used to sit at the top level of the rail as siblings of Finance. They
    // are one module's four screens, and this is where decision 1 changes the most.
    id: 'clinical-study',
    labelKey: 'nav.clinicalStudy',
    path: '/clinical-study/studies',
    basePath: '/clinical-study',
    icon: FlaskConical,
    /*
     * WAS `{ kind: 'always' }` UNTIL 2026-09-14, and that is the defect this entry now closes.
     *
     * `always` meant every authenticated account saw this module and all four of its sections —
     * a finance-only colleague, an account in an organization that never bought the module, an
     * external investigator. Clicking it did not produce a polite refusal: the studies page
     * rendered a raw red technical line reading that the service answered 403, beside a Retry
     * button that fails identically. Three independent audits reached that same scenario.
     *
     * The catalog used to explain the absence of a real gate by saying the capability-fetching
     * plumbing did not exist. That was false: `GET /v1/clinical-study/me/permissions` ships today
     * and two pages inside this very module already consume it. It had simply never been connected
     * to navigation.
     *
     * `study:read` is the right code to gate on rather than an exhaustive list, for a measured
     * reason: all three seeded clinical-study roles hold it, so nobody legitimate is hidden, and
     * the module's landing screen is the study list, which needs exactly this. It is also the code
     * guarding the permissions read itself — so an account without it is refused that read, the
     * gate fails closed, and the entry disappears. The two facts agree by construction.
     */
    gate: { kind: 'permission', anyOf: ['clinical-study:study:read'] },
    sections: [
      {
        id: 'studies',
        labelKey: 'nav.clinicalStudySections.studies',
        path: '/clinical-study/studies',
      },
      {
        id: 'pool',
        labelKey: 'nav.clinicalStudySections.pool',
        path: '/clinical-study/treatment-date-pool',
      },
      {
        id: 'quick-reference',
        labelKey: 'nav.clinicalStudySections.quickReference',
        path: '/clinical-study/quick-reference',
      },
      {
        id: 'oversight',
        labelKey: 'nav.clinicalStudySections.oversight',
        path: '/clinical-study/oversight',
      },
    ],
  },
  {
    // «Strumenti» (#387, decision 98): after the work modules, before Admin. Its sections are added
    // with their screens. The gate is identity documents' until the «org members» rule (decision 100).
    id: 'tools',
    labelKey: 'nav.tools',
    path: '/tools',
    basePath: '/tools',
    icon: Wrench,
    gate: TOOLS_GATE,
    sections: [
      { id: 'overview', labelKey: 'nav.toolsSections.overview', path: '/tools', end: true },
      { id: 'images', labelKey: 'nav.toolsSections.images', path: '/tools/images' },
      { id: 'pdf', labelKey: 'nav.toolsSections.pdf', path: '/tools/pdf' },
      ...(CLEAN_TEXT_AVAILABLE
        ? [
            {
              id: 'clean-text',
              labelKey: 'nav.toolsSections.cleanText' as const,
              path: '/tools/clean-text',
            },
          ]
        : []),
      {
        id: 'identity-documents',
        labelKey: 'nav.toolsSections.identityDocuments',
        path: '/tools/identity-documents',
      },
      { id: 'device-test', labelKey: 'nav.toolsSections.deviceTest', path: '/tools/device-test' },
    ],
  },
  {
    // «Archivio» (#788, decision 189): the overview of where Drive departs from the filing rules,
    // and the rules themselves. Open to org members; each page has its own read permission.
    id: 'archive',
    labelKey: 'nav.archive',
    path: '/archive',
    basePath: '/archive',
    icon: FolderOpen,
    gate: ARCHIVE_GATE,
    sections: [
      { id: 'overview', labelKey: 'nav.archiveSections.overview', path: '/archive', end: true },
      { id: 'rules', labelKey: 'nav.archiveSections.rules', path: '/archive/rules' },
    ],
  },
  {
    // «Comunicazioni» (#739, decision 181): the Director approves the onboarding emails and reads
    // their log. After the work modules, before Admin; gated by `communications:approve`.
    id: 'communications',
    labelKey: 'nav.communications',
    path: '/communications/approvals',
    basePath: '/communications',
    icon: Mail,
    gate: COMMUNICATIONS_GATE,
    sections: [
      {
        id: 'approvals',
        labelKey: 'nav.communicationsSections.approvals',
        path: '/communications/approvals',
      },
      { id: 'log', labelKey: 'nav.communicationsSections.log', path: '/communications/log' },
    ],
  },
  {
    // SL-0023, then the administration area by place (decisions 60 and 61). Anyone who
    // administers a place sees it (FR-AD-3), and it opens on the area, which picks the place:
    // the active organization when the caller administers it, else their broadest one. Its
    // sections follow that place and are supplied at runtime (`dynamicSections`), so none are
    // declared here. `AdministrationAreaGate` gates the route; today's operator screens keep
    // `AdminGate` until slice 2 replaces them (FR-AD-12).
    id: 'admin',
    labelKey: 'nav.admin',
    path: '/admin',
    basePath: '/admin',
    icon: ShieldCheck,
    gate: { kind: 'administrator' },
    sections: [],
  },
];

/**
 * Screens that are reachable and nameable but are not modules and must not appear in the rail.
 *
 * `/profile` belongs to the account, is reached from the account disclosure at the rail's foot, and
 * putting it in the rail would advertise a settings page as a module. `/guide` and `/status` are
 * the footer's service pages, reached from its links. They are declared here rather than left out
 * entirely so the breadcrumb and the command palette can still name them — the alternative is a
 * breadcrumb that goes blank on a real screen.
 */
export const AUXILIARY_SCREENS = [
  { id: 'profile', labelKey: 'nav.profile', basePath: '/profile' },
  { id: 'guide', labelKey: 'nav.guide', basePath: '/guide' },
  { id: 'status', labelKey: 'nav.serviceStatus', basePath: '/status' },
] as const satisfies ReadonlyArray<{
  id: string;
  labelKey: ShellTranslationKey;
  basePath: string;
}>;
